import React from "react";
import {
  SpaceBetween,
  FormField,
  Input,
  Grid,
} from "@amzn/awsui-components-react";
import { getAllCountryData, getAllCurrencyData } from "country-currency-utils";
import {
  Ard,
  BillingModeType,
  ProductType,
  TemplateType,
  TermType,
} from "@amzn/fuse-accounting-central-datastore-client";
import { ValidatedSelectField } from "./ValidatedSelectField";
import { useValidationUtils } from "./ValidationUtils";
import { SUPPORTED_COUNTRY_CODES } from "src/components/constants";

// Static data processed once
const COUNTRY_OPTIONS = getAllCountryData()
  .filter((country) => SUPPORTED_COUNTRY_CODES.includes(country.countryCode))
  .map((country) => ({
    label: `${country.countryCode} - ${country.name}`,
    value: country.countryCode,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const CURRENCY_OPTIONS = getAllCurrencyData().map((currency) => ({
  label: `${currency.currencyCode} - ${currency.name}`,
  value: currency.currencyCode,
}));

const PRODUCT_OPTIONS = Object.values(ProductType).map((value) => ({
  label: value,
  value,
}));

const TEMPLATE_OPTIONS = Object.values(TemplateType).map((value) => ({
  label: value,
  value,
}));

const TERM_OPTIONS = Object.values(TermType).map((value) => ({
  label: value,
  value,
}));

const BILLING_MODE_OPTIONS = Object.values(BillingModeType).map((value) => ({
  label: value,
  value,
}));

// Static field options map
const FIELD_OPTIONS_MAP = {
  product: PRODUCT_OPTIONS,
  template: TEMPLATE_OPTIONS,
  term: TERM_OPTIONS,
  businessCountryCode: COUNTRY_OPTIONS,
  buyingCustomerCountryCode: COUNTRY_OPTIONS,
  functionalCurrency: CURRENCY_OPTIONS,
  localCurrency: CURRENCY_OPTIONS,
  billingMode: BILLING_MODE_OPTIONS,
} as const;

interface GeneralInformationSectionProps {
  formData: Ard;
  handleChange: (field: keyof Ard, value: string) => void;
  getUniqueValues: (
    field: keyof Ard,
  ) => Array<{ label: string; value: string }>;
  validationUtils: ReturnType<typeof useValidationUtils>;
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void;
  onBlur?: () => void;
}

type FieldConfig = Readonly<{
  fieldName: keyof Ard;
  label: string;
  placeholder: string;
}>;

const generalInfoFields: FieldConfig[] = [
  { fieldName: "product", label: "Product", placeholder: "Select product" },
  { fieldName: "template", label: "Template", placeholder: "Select template" },
  { fieldName: "term", label: "Term", placeholder: "Select term" },
  {
    fieldName: "billingMode",
    label: "Billing Mode",
    placeholder: "Select billing mode",
  },
  {
    fieldName: "businessCountryCode",
    label: "Business Country Code",
    placeholder: "Select country code",
  },
  {
    fieldName: "buyingCustomerCountryCode",
    label: "Customer Country Code",
    placeholder: "Select country code",
  },
  {
    fieldName: "functionalCurrency",
    label: "Functional Currency",
    placeholder: "Select currency",
  },
  {
    fieldName: "localCurrency",
    label: "Local Currency",
    placeholder: "Select currency",
  },
];

export const GeneralInformationSection: React.FC<
  GeneralInformationSectionProps
> = ({
  formData,
  handleChange,
  getUniqueValues,
  validationUtils,
  onFocus,
  onBlur,
}) => {
  const getFieldOptions = (fieldName: keyof Ard) => {
    return (
      FIELD_OPTIONS_MAP[fieldName as keyof typeof FIELD_OPTIONS_MAP] ||
      getUniqueValues(fieldName)
    );
  };

  return (
    <SpaceBetween direction="vertical" size="s">
      <FormField label="ARD ID">
        <Input
          value={formData.id || ""}
          onChange={(e) => handleChange("id", e.detail.value)}
          disabled
        />
      </FormField>

      <SpaceBetween direction="vertical" size="s">
        <Grid
          gridDefinition={[
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
          ]}
        >
          {generalInfoFields
            .slice(0, 4)
            .map(({ fieldName, label, placeholder }) => (
              <ValidatedSelectField
                key={fieldName}
                label={label}
                fieldName={fieldName}
                value={(formData[fieldName as keyof Ard] as string) || null}
                options={getFieldOptions(fieldName as keyof Ard)}
                placeholder={placeholder}
                onChange={(value) =>
                  handleChange(fieldName as keyof Ard, value)
                }
                validationUtils={validationUtils}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            ))}
        </Grid>
        <Grid
          gridDefinition={[
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
          ]}
        >
          {generalInfoFields
            .slice(4, 8)
            .map(({ fieldName, label, placeholder }) => (
              <ValidatedSelectField
                key={fieldName}
                label={label}
                fieldName={fieldName}
                value={(formData[fieldName as keyof Ard] as string) || null}
                options={getFieldOptions(fieldName as keyof Ard)}
                placeholder={placeholder}
                onChange={(value) =>
                  handleChange(fieldName as keyof Ard, value)
                }
                validationUtils={validationUtils}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            ))}
        </Grid>
      </SpaceBetween>
    </SpaceBetween>
  );
};
